import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import {
  CardBrandType,
  GatewayProviderType,
} from '../../../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { addCreditCardTokenOperation } from '../operations/add-credit-card-token-operation';

export interface AddCreditCardTokenDialogProps {
  visible: boolean;
  onHide: () => void;
  userId: number;
}

type CardBrandOption = { label: string; value: CardBrandType };
type MonthOption = { label: string; value: number };
type YearOption = { label: string; value: number };

const cardBrandLabel = (brand: CardBrandType): string => {
  switch (brand) {
    case CardBrandType.VISA:
      return 'Visa';
    case CardBrandType.MASTERCARD:
      return 'Mastercard';
    case CardBrandType.AMERICAN_EXPRESS:
      return 'American Express';
    case CardBrandType.DINERS_CLUB:
      return 'Diners';
    case CardBrandType.ISRACARD:
      return 'Isracard';
    case CardBrandType.JCB:
      return 'JCB';
    case CardBrandType.DISCOVER:
      return 'Discover';
    case CardBrandType.MAESTRO:
      return 'Maestro';
    case CardBrandType.PRIVATE_LABEL:
      return 'Private Label';
    default:
      return 'לא ידוע';
  }
};

export const AddCreditCardTokenDialog: React.FC<
  AddCreditCardTokenDialogProps
> = ({ visible, onHide, userId }) => {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [expiryMonth, setExpiryMonth] = useState<number | null>(null);
  const [expiryYear, setExpiryYear] = useState<number | null>(null);
  const [cardBrand, setCardBrand] = useState<CardBrandType | null>(null);

  useEffect(() => {
    if (!visible) return;
    setLoading(false);
    setToken('');
    setCardLast4('');
    setExpiryMonth(null);
    setExpiryYear(null);
    setCardBrand(null);
  }, [visible]);

  const brandOptions: CardBrandOption[] = useMemo(
    () =>
      Object.values(CardBrandType).map((value) => ({
        value,
        label: cardBrandLabel(value),
      })),
    [],
  );

  const monthOptions: MonthOption[] = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const v = i + 1;
        return { value: v, label: String(v).padStart(2, '0') };
      }),
    [],
  );

  const yearOptions: YearOption[] = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 15 }, (_, i) => {
      const v = current + i;
      return { value: v, label: String(v) };
    });
  }, []);

  const isApproveDisabled =
    loading ||
    !token ||
    !cardLast4 ||
    !expiryMonth ||
    !expiryYear ||
    !cardBrand;

  const handleApprove = async () => {
    if (!expiryMonth || !expiryYear || !cardBrand) return;

    const success = await addCreditCardTokenOperation(
      userId,
      {
        token,
        cardLast4,
        expiryMonth,
        expiryYear,
        cardBrand,
      },
      setLoading,
      () => {
        onHide();
      },
    );

    if (success) {
      onHide();
    }
  };

  return (
    <Dialog
      header="הוספת כרטיס אשראי"
      visible={visible}
      onHide={onHide}
      style={{ width: '520px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div
          style={{
            fontSize: '13px',
            color: '#6b7280',
            lineHeight: 1.5,
          }}
        >
          הוספה ידנית של טוקן כרטיס אשראי ({GatewayProviderType.PELECARD}).
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '14px', fontWeight: 600 }}>
            טוקן (10 ספרות)
          </label>
          <InputText
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="XXXXXXXX"
            inputMode="numeric"
            maxLength={10}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '14px', fontWeight: 600 }}>
            4 ספרות אחרונות
          </label>
          <InputText
            value={cardLast4}
            onChange={(e) => setCardLast4(e.target.value)}
            placeholder="1234"
            inputMode="numeric"
            maxLength={4}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <label style={{ fontSize: '14px', fontWeight: 600 }}>
              חודש תפוגה
            </label>
            <Dropdown
              value={expiryMonth}
              options={monthOptions}
              onChange={(e) => setExpiryMonth(e.value)}
              placeholder="בחר חודש"
            />
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <label style={{ fontSize: '14px', fontWeight: 600 }}>
              שנת תפוגה
            </label>
            <Dropdown
              value={expiryYear}
              options={yearOptions}
              onChange={(e) => setExpiryYear(e.value)}
              placeholder="בחר שנה"
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '14px', fontWeight: 600 }}>
            מותג כרטיס
          </label>
          <Dropdown
            value={cardBrand}
            options={brandOptions}
            onChange={(e) => setCardBrand(e.value)}
            placeholder="בחר מותג"
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: '4px',
          }}
        >
          <Button
            label="ביטול"
            onClick={onHide}
            className="p-button-text"
            disabled={loading}
          />
          <Button
            label="אישור"
            icon="pi pi-check"
            onClick={handleApprove}
            loading={loading}
            disabled={isApproveDisabled}
            className="p-button-success"
          />
        </div>
      </div>
    </Dialog>
  );
};
